import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input, OnChanges, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import { ChartModule, UIChart } from 'primeng/chart';
import { Subject, takeUntil } from 'rxjs';
import { AlarmService } from '../../../services/alarm.service';
import { ThemeService } from '../../../services/theme.service';
import { AlarmType } from '../../../types/enums/alarm.enum';
import { PyChart, PyChartData } from '../../../types/interfaces/charts.interface';

@Component({
  selector: 'app-chart-pie',
  standalone: true,
  imports: [
    ChartModule,
  ],
  templateUrl: './pie-chart.html',
  styleUrl: './pie-chart.css',
})
export class ChartPieComponent implements OnInit, OnChanges {
  @Input()
  public pyChartData!: PyChart;
  @Input()
  public legendPosition: 'top' | 'bottom' | 'left' | 'right' = 'right';
  public data: ChartData<'pie'> = {
    labels: [],
    datasets: [],
  };
  public options: ChartOptions<'pie'> = {};
  @ViewChild('pieChart')
  private pieChart!: UIChart;
  private _themeObserver: MutationObserver | null = null;
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _alarmService: AlarmService,
    private readonly _themeService: ThemeService,
  ) {
  }

  public ngOnInit(): void {
    this.initChart();

    this._themeService.theme$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initChart();
      });
  }

  public ngOnChanges(): void {
    this.initChart();
  }

  public onChartDataSelect(event: any): void {
    const index: number | undefined =
      event?.element?.index ??
      event?.element?._index;

    if (index === undefined || !this.pieChart?.chart) {
      return;
    }

    this.toggleSlice(index);
  }

  private initChart(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    const bodyStyle: CSSStyleDeclaration = getComputedStyle(document.body);
    const rootStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement);

    const textColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--text-primary',
        '--app-text-color',
      ],
      '#111827',
    );

    const borderColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-border',
        '--border-color',
      ],
      'rgba(17, 24, 39, 0.2)',
    );

    const chartColors: string[] = this.getChartColors(this.pyChartData?.data);
    const labelsWithValues: string[] = this.getLabelsWithValues();

    this.data = {
      labels: labelsWithValues,
      datasets: [
        {
          data: this.pyChartData?.data.map((item) => item.value),
          backgroundColor: chartColors,
          hoverBackgroundColor: chartColors,
          borderColor,
          borderWidth: 1,
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 8,
      },
      plugins: {
        legend: {
          display: true,
          position: this.legendPosition,
          align: 'center',
          onClick: (_event, legendItem) => {
            const index = legendItem.index;

            if (index === undefined || !this.pieChart?.chart) {
              return;
            }

            this.toggleSlice(index);
          },
          labels: {
            usePointStyle: true,
            color: textColor,
            padding: 18,
            boxWidth: 10,
            boxHeight: 10,
          },
        },
        title: {
          display: this.pyChartData?.title !== '',
          text: this.pyChartData?.title,
          color: textColor,
        },
      },
    };

    this._changeDetectorRef.markForCheck();
  }

  private toggleSlice(index: number): void {
    let data: PyChartData | undefined = this.pyChartData?.data[index];
    if (!data) return;
    const chart = this.pieChart.chart;
    const legend = chart.legend.legendItems[index];
    const isHidden = legend.hidden;
    const alarmType: AlarmType = (data.key ?? data.label) as AlarmType;

    this._alarmService.toggleAlarmTypeFilter(alarmType, isHidden);

    chart.toggleDataVisibility(index);
    chart.update();
    chart.canvas.click();
  }

  private getLabelsWithValues(): string[] {
    return this.pyChartData?.data.map((item) => {
      return `${ item.label }: ${ item.value }`;
    });
  }

  private getChartColors(data: PyChartData[]): string[] {
    let colors: string[] = [];

    let defaultColors: string[] = [
      '#ef4444',
      '#f59e0b',
      '#22c55e',
      '#3b82f6',
      '#94a3b8',
    ];

    let index: number = 0;
    data.forEach((item) => {
      if (item.color) {
        colors.push(item.color);
      } else {
        colors.push(defaultColors[index % defaultColors.length]);
        index++;
      }

    });

    return colors
  }

  private getCssVariable(
    bodyStyle: CSSStyleDeclaration,
    rootStyle: CSSStyleDeclaration,
    variableNames: string[],
    fallback: string,
  ): string {
    for (const variableName of variableNames) {
      const bodyValue: string = bodyStyle.getPropertyValue(variableName)
        .trim();

      if (bodyValue !== '') {
        return bodyValue;
      }

      const rootValue: string = rootStyle.getPropertyValue(variableName)
        .trim();

      if (rootValue !== '') {
        return rootValue;
      }
    }

    return fallback;
  }
}
